import { useMemo } from 'react';

import {
    AviationDocumentCategory,
    CategoryComplianceStats,
} from '@/features/end-of-lease/types';

import { IATA_BINDER_DISPLAY_MAP } from '@/lib/constants';
import { formatDate } from '@/lib/utils';

export type IATAFolderData = {
    letter: string;
    name: string;
    progress: number;
    status: 'complete' | 'in-progress';
    assignee: string;
    priority: 'high' | 'medium' | 'low';
    documents: number;
    lastUpdate: string;
    complianceDetails?: {
        totalChecks: number;
        passedChecks: number;
        failedChecks: number;
        totalItems: number;
    };
};

function createFolderData(
    category: AviationDocumentCategory,
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData {
    const { letter, name, assignee, priority } =
        IATA_BINDER_DISPLAY_MAP[category];

    const complianceData = complianceSummary?.find(
        (item) => item.category === category,
    );

    const progress = complianceData?.progressPercentage || 0;
    const status = progress >= 90 ? 'complete' : 'in-progress';
    const lastUpdate = complianceData?.lastUpdated
        ? formatDate(complianceData.lastUpdated)
        : 'No activity';

    return {
        letter,
        name,
        progress,
        status,
        assignee,
        priority,
        documents: complianceData?.totalItems || 0,
        lastUpdate,
        complianceDetails: complianceData
            ? {
                  totalChecks: complianceData.totalChecks,
                  passedChecks: complianceData.passedChecks,
                  failedChecks: complianceData.failedChecks,
                  totalItems: complianceData.totalItems,
              }
            : undefined,
    };
}

function getCategoryFromLetter(
    letter: string,
): AviationDocumentCategory | undefined {
    return Object.entries(IATA_BINDER_DISPLAY_MAP).find(
        ([_, value]) => value.letter === letter,
    )?.[0] as AviationDocumentCategory | undefined;
}

export function useIATAFolderByLetter(
    letter: string,
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData | undefined {
    const category = getCategoryFromLetter(letter);

    return useMemo(() => {
        if (!category) return undefined;
        return createFolderData(category, complianceSummary);
    }, [category, complianceSummary]);
}

export function useIATAFolders(
    categories: AviationDocumentCategory[],
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData[] {
    return useMemo(() => {
        return categories.map((category) =>
            createFolderData(category, complianceSummary),
        );
    }, [categories, complianceSummary]);
}
